import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap, finalize } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { endpoints } from '../../../../environments/endpoints';
import { AuthResponse, AuthenticatedAuthResponse, GoogleLoginRequest, isAuthenticatedResponse, LoginModel, NewPassword, PasswordReset, RegisterUser, User, UserSession, ValidateEmail } from '@core/models';
import { RefreshTokenService } from './refresh-token.service';
import { suppressGlobalErrorFeedback } from '@core/interceptors/feedback-context';
import { TwoFactorAuthorizationStore } from './two-factor-authorization.store';
import { TwoFactorWorkflowStore } from './two-factor-workflow.store';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private sessionKey = 'fibeacon.session';
  private _isAuthenticated = new BehaviorSubject<boolean>(!!this.getSessionToken());
  isAuthenticated$ = this._isAuthenticated.asObservable();

  constructor(
    private http: HttpClient,
    private refreshTokenService: RefreshTokenService,
    private twoFactorAuthorizations: TwoFactorAuthorizationStore,
    private twoFactorWorkflow: TwoFactorWorkflowStore
  ) { }

  public getSession(): UserSession | undefined {
    try {
      const raw = localStorage.getItem(this.sessionKey);
      if (!raw) return undefined;
      const parsed = JSON.parse(raw) as UserSession | { userSession: UserSession };
      // support storing either the userSession object directly or the full response
      if ((parsed as any).userSession) return (parsed as any).userSession;
      return parsed as UserSession;
    } catch {
      return undefined;
    }
  }

  public setSession(session: UserSession | undefined) {
    if (session) {
      localStorage.setItem(this.sessionKey, JSON.stringify(session));
      this._isAuthenticated.next(true);
    } else {
      localStorage.removeItem(this.sessionKey);
      this.twoFactorAuthorizations.clear();
      this.twoFactorWorkflow.clearLogin();
      this._isAuthenticated.next(false);
    }
  }

  private getSessionToken(): string | null {
    const s = this.getSession();
    return s?.token ?? null;
  }

  public isAuthenticated(): boolean {
    return !!this.getSessionToken();
  }

  login(model: Partial<LoginModel>): Observable<AuthResponse> {
    const payload: LoginModel = {
      userName: model.userName || '',
      password: model.password || '',
      device: model.device || navigator.userAgent || 'web',
      captcha: model.captcha || '',
      useCaptcha: !!model.useCaptcha,
      applicationId: environment.applicationId
    };

    const url = `${environment.authApiUrl}/${endpoints.auth.v1.authLogin}`;

    return this.http.post<AuthResponse>(url, payload).pipe(
      tap((response) => this.persistAuthenticatedResponse(response))
    );
  }

  continueWithGoogle(googleLoginRequest: GoogleLoginRequest): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>(
        `${environment.authApiUrl}/${endpoints.auth.v1.authContinueWithGoogle}`,
        googleLoginRequest,
        { context: suppressGlobalErrorFeedback() }
      )
      .pipe(
        tap((response) => this.persistAuthenticatedResponse(response))
      );

  }

  removeSession() {
    this.setSession(undefined);
  }

  passwordReset(passwordReset: PasswordReset) {
    return this.http.post(
      `${environment.authApiUrl}/${endpoints.auth.v1.forgotPasswordPasswordReset}`,
      passwordReset
    )
  }

  newPassword(newPassword: NewPassword) {
    return this.http.post(
      `${environment.authApiUrl}/${endpoints.auth.v1.authForgotPasswordNewPassword}`,
      newPassword
    )
  }

  register(registerUser: RegisterUser) {
    return this.http.post<User>(
      `${environment.authApiUrl}/${endpoints.auth.v1.authRegister}`,
      registerUser
    );
  }

  persistAuthenticatedResponse(
    response: AuthResponse
  ): response is AuthenticatedAuthResponse {
    if (!isAuthenticatedResponse(response)) return false;

    const session = response.userSession;
    if (!session?.refreshToken || !session.token) {
      throw new Error('Malformed authenticated response.');
    }

    this.refreshTokenService.set(session.refreshToken, session.expirationDate);
    session.refreshToken = undefined;
    this.twoFactorAuthorizations.clear();
    this.twoFactorWorkflow.clearLogin();
    this.setSession(session);
    return true;
  }

  validateEmail(validateEmail: ValidateEmail) {
    return this.http.post(
      `${environment.authApiUrl}/${endpoints.auth.v1.authValidateEmail}`,
      validateEmail
    )
  }

  refreshToken(userSession: UserSession) {
    return this.http.post<UserSession>(
      `${environment.authApiUrl}/${endpoints.auth.v1.authRefreshToken}`,
      userSession
    );
  }

  logout(userSession?: UserSession) {
    const payload: UserSession | any = userSession ?? this.getSession() ?? {} as UserSession;
    payload.refreshToken = payload.refreshToken ?? this.refreshTokenService.get();
    return this.http.post<UserSession>(
      `${environment.authApiUrl}/${endpoints.auth.v1.authLogout}`,
      payload
    ).pipe(
      finalize(() => {
        this.refreshTokenService.remove();
        this.setSession(undefined);
      })
    );
  }
}
